import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Activity,
  GitBranch,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Edit,
  Save,
  Plus,
  Search,
  Play,
  Pause,
  Square,
  Check,
  X,
  Code,
  FileText,
  Users,
  Calendar,
  Clock,
  Workflow,
  RefreshCw,
  Loader,
  AlertCircle,
  User,
  Zap,
  Settings,
  Database,
  Globe,
  Bot,
  Monitor,
  Maximize,
  Filter,
  Download,
  Upload,
  BarChart,
  Shield,
  Package,
  Layers,
  Copy
} from 'lucide-react';

// Import dynamique de tous les fichiers JSON du répertoire processes
const processContext = require.context('../data/processes', false, /\.json$/);

// Mapping des icônes par type de processus
const PROCESS_ICONS = {
  'order': FileText,
  'record': Activity,
  'replay': RefreshCw,
  'script': Code,
  'support': Users,
  'user': User,
  'incident': AlertTriangle,
  'management': Settings,
  'generation': Zap,
  'validation': CheckCircle,
  'capture': Upload,
  'execution': Play,
  'monitoring': Monitor,
  'security': Shield,
  'data': Database,
  'integration': Layers,
  'default': Workflow
};

// Validation de structure d'un processus
const validateProcessStructure = (data, filename) => {
  const requiredFields = ['id', 'name', 'states'];
  const errors = [];
  
  // Vérifier les champs obligatoires
  for (const field of requiredFields) {
    if (!data[field]) {
      errors.push(`Champ obligatoire manquant: ${field}`);
    }
  }
  
  // Vérifier la structure des états
  if (data.states && Array.isArray(data.states)) {
    if (data.states.length === 0) {
      errors.push('Le processus doit avoir au moins un état');
    }
    
    // Vérifier qu'il y a un état initial
    const hasInitialState = data.states.some(state => state.isInitial);
    if (!hasInitialState) {
      errors.push('Le processus doit avoir un état initial');
    }
    
    // Vérifier que chaque état a un id et un nom
    data.states.forEach((state, index) => {
      if (!state.id) errors.push(`État ${index}: id manquant`);
      if (!state.name) errors.push(`État ${index}: nom manquant`);
    });
  } else {
    errors.push('Le champ states doit être un tableau');
  }
  
  // Vérifier les transitions si présentes
  if (data.transitions && Array.isArray(data.transitions)) {
    data.transitions.forEach((transition, index) => {
      if (!transition.from) errors.push(`Transition ${index}: from manquant`);
      if (!transition.to) errors.push(`Transition ${index}: to manquant`);
      if (!transition.name) errors.push(`Transition ${index}: name manquant`);
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

// Déterminer l'icône appropriée pour un processus
const getProcessIcon = (process) => {
  // Chercher dans le nom du fichier ou le nom du processus
  const searchString = `${process.filename} ${process.name} ${process.category || ''}`.toLowerCase();
  
  for (const [key, icon] of Object.entries(PROCESS_ICONS)) {
    if (searchString.includes(key)) {
      return icon;
    }
  }
  
  return PROCESS_ICONS.default;
};

// Extraire les métadonnées d'un processus
const extractProcessMetadata = (data, filename) => {
  return {
    filename,
    id: data.id || filename.replace('.json', ''),
    name: data.name || data.metadata?.title || filename.replace(/_/g, ' ').replace('.json', ''),
    description: data.description || data.metadata?.description || '',
    version: data.version || data.metadata?.version || '1.0',
    category: data.category || data.metadata?.category || 'Non catégorisé',
    owner: data.owner || data.metadata?.author || 'Non défini',
    lastUpdate: data.metadata?.lastUpdate || new Date().toISOString(),
    statesCount: data.states?.length || 0,
    transitionsCount: data.transitions?.length || 0,
    tags: data.tags || extractTags(data),
    icon: getProcessIcon({ filename, name: data.name, category: data.category })
  };
};

// Extraire des tags automatiquement
const extractTags = (process) => {
  const tags = [];
  
  if (process.metadata?.ai_processing) tags.push('IA');
  if (process.metadata?.critical) tags.push('Critique');
  if (process.states?.some(s => s.metadata?.manual_review)) tags.push('Revue manuelle');
  if (process.states?.some(s => s.metadata?.notifications)) tags.push('Notifications');
  
  return tags;
};

// Composants UI de base
const Card = ({ children, className = '', onClick, onMouseDown, style, ...props }) => (
  <div 
    className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}
    onClick={onClick}
    onMouseDown={onMouseDown}
    style={style}
    {...props}
  >
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700',
    danger: 'bg-red-500 text-white hover:bg-red-600'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Badge pour les tags
const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800'
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Composant de diagramme de processus interactif
const ProcessDiagram = ({ process }) => {
  const svgRef = useRef(null);
  const [selectedState, setSelectedState] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hoveredTransition, setHoveredTransition] = useState(null);

  // Réinitialiser quand le processus change
  useEffect(() => {
    setSelectedState(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setHoveredTransition(null);
  }, [process.id]);

  // Configuration du layout
  const NODE_WIDTH = 180;
  const NODE_HEIGHT = 100;
  const HORIZONTAL_SPACING = 280;
  const VERTICAL_SPACING = 150;

  // Calcul automatique du layout
  const calculateLayout = useMemo(() => {
    if (!process.states || process.states.length === 0) return { nodes: [], edges: [] };

    const states = process.states;
    const transitions = process.transitions || [];
    
    // Créer un graphe pour analyser les connexions
    const graph = {};
    states.forEach(state => {
      graph[state.id] = {
        ...state,
        incoming: [],
        outgoing: [],
        level: -1,
        x: 0,
        y: 0
      };
    });
    
    // Analyser les transitions
    transitions.forEach(transition => {
      if (graph[transition.from] && graph[transition.to]) {
        graph[transition.from].outgoing.push(transition);
        graph[transition.to].incoming.push(transition);
      }
    });
    
    // Calculer les niveaux (BFS)
    const queue = [];
    const visited = new Set();
    
    // Trouver les états initiaux
    states.forEach(state => {
      if (state.isInitial || graph[state.id].incoming.length === 0) {
        queue.push(state.id);
        graph[state.id].level = 0;
        visited.add(state.id);
      }
    });
    
    // BFS pour assigner les niveaux
    while (queue.length > 0) {
      const currentId = queue.shift();
      const current = graph[currentId];
      
      current.outgoing.forEach(transition => {
        if (!visited.has(transition.to)) {
          visited.add(transition.to);
          graph[transition.to].level = current.level + 1;
          queue.push(transition.to);
        }
      });
    }
    
    // Grouper par niveau
    const levels = {};
    Object.values(graph).forEach(node => {
      const level = node.level === -1 ? 0 : node.level;
      if (!levels[level]) levels[level] = [];
      levels[level].push(node);
    });
    
    // Calculer les positions
    const nodes = [];
    Object.entries(levels).forEach(([level, levelNodes]) => {
      const y = parseInt(level) * VERTICAL_SPACING + 50;
      const totalWidth = levelNodes.length * HORIZONTAL_SPACING;
      const startX = (800 - totalWidth) / 2 + HORIZONTAL_SPACING / 2;
      
      levelNodes.forEach((node, index) => {
        node.x = startX + index * HORIZONTAL_SPACING;
        node.y = y;
        nodes.push({
          ...node,
          width: NODE_WIDTH,
          height: NODE_HEIGHT
        });
      });
    });
    
    // Créer les edges
    const edges = transitions.map(transition => {
      const from = graph[transition.from];
      const to = graph[transition.to];
      
      if (!from || !to) return null;
      
      return {
        ...transition,
        x1: from.x,
        y1: from.y + NODE_HEIGHT / 2,
        x2: to.x,
        y2: to.y - NODE_HEIGHT / 2
      };
    }).filter(Boolean);
    
    return { nodes, edges };
  }, [process]);

  const { nodes, edges } = calculateLayout;

  // Fonction pour dessiner une flèche courbe
  const getPathData = (edge) => {
    const midY = (edge.y1 + edge.y2) / 2;
    return `M ${edge.x1} ${edge.y1} C ${edge.x1} ${midY}, ${edge.x2} ${midY}, ${edge.x2} ${edge.y2}`;
  };

  // Gérer le zoom
  const handleZoom = (delta) => {
    setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };

  return (
    <div className="relative bg-gray-50 rounded-lg overflow-hidden" style={{ height: '600px' }}>
      {/* Contrôles */}
      <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleZoom(0.1)}
          className="bg-white"
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleZoom(-0.1)}
          className="bg-white"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
          className="bg-white"
        >
          <Maximize className="h-4 w-4" />
        </Button>
      </div>

      {/* SVG du diagramme */}
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        className="cursor-grab active:cursor-grabbing"
      >
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Définition des marqueurs pour les flèches */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="10"
              refY="5"
              orient="auto"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#6B7280" />
            </marker>
          </defs>

          {/* Dessiner les transitions */}
          {edges.map((edge, index) => (
            <g key={index}>
              <path
                d={getPathData(edge)}
                fill="none"
                stroke={hoveredTransition === index ? "#3B82F6" : "#9CA3AF"}
                strokeWidth={hoveredTransition === index ? 3 : 2}
                markerEnd="url(#arrowhead)"
                className="transition-all duration-200"
                onMouseEnter={() => setHoveredTransition(index)}
                onMouseLeave={() => setHoveredTransition(null)}
              />
              {hoveredTransition === index && (
                <text
                  x={(edge.x1 + edge.x2) / 2}
                  y={(edge.y1 + edge.y2) / 2}
                  textAnchor="middle"
                  className="fill-gray-700 text-sm font-medium"
                  dy="-10"
                >
                  {edge.name}
                </text>
              )}
            </g>
          ))}

          {/* Dessiner les états */}
          {nodes.map((node) => {
            const isSelected = selectedState === node.id;
            const StateIcon = node.isFinal ? CheckCircle : 
                            node.isInitial ? Play : 
                            node.metadata?.manual_review ? Users :
                            node.metadata?.ai_processing ? Bot :
                            Activity;

            return (
              <g
                key={node.id}
                transform={`translate(${node.x - NODE_WIDTH / 2}, ${node.y - NODE_HEIGHT / 2})`}
                className="cursor-pointer"
                onClick={() => setSelectedState(isSelected ? null : node.id)}
              >
                {/* Ombre */}
                <rect
                  x="2"
                  y="2"
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  rx="8"
                  fill="rgba(0,0,0,0.1)"
                />
                
                {/* Rectangle principal */}
                <rect
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  rx="8"
                  fill={isSelected ? "#3B82F6" : "#FFFFFF"}
                  stroke={isSelected ? "#3B82F6" : "#E5E7EB"}
                  strokeWidth="2"
                  className="transition-all duration-200"
                />
                
                {/* Icône */}
                <StateIcon
                  x="10"
                  y="10"
                  width="24"
                  height="24"
                  className={isSelected ? "text-white" : "text-gray-600"}
                />
                
                {/* Nom de l'état */}
                <text
                  x={NODE_WIDTH / 2}
                  y="45"
                  textAnchor="middle"
                  className={`text-sm font-semibold ${isSelected ? 'fill-white' : 'fill-gray-900'}`}
                >
                  {node.name}
                </text>
                
                {/* Description */}
                {node.description && (
                  <text
                    x={NODE_WIDTH / 2}
                    y="65"
                    textAnchor="middle"
                    className={`text-xs ${isSelected ? 'fill-blue-100' : 'fill-gray-500'}`}
                  >
                    {node.description.length > 25 
                      ? node.description.substring(0, 25) + '...' 
                      : node.description}
                  </text>
                )}
                
                {/* Badge SLA si présent */}
                {node.metadata?.slaMinutes && (
                  <g transform={`translate(${NODE_WIDTH - 40}, 10)`}>
                    <rect
                      width="30"
                      height="20"
                      rx="10"
                      fill={isSelected ? "#1E40AF" : "#FEF3C7"}
                    />
                    <text
                      x="15"
                      y="15"
                      textAnchor="middle"
                      className={`text-xs font-medium ${isSelected ? 'fill-white' : 'fill-yellow-800'}`}
                    >
                      {node.metadata.slaMinutes}m
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Panneau de détails */}
      {selectedState && (
        <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-md">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">
              {nodes.find(n => n.id === selectedState)?.name}
            </h4>
            <button
              onClick={() => setSelectedState(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            {nodes.find(n => n.id === selectedState)?.description}
          </p>
          
          {/* Métadonnées */}
          {nodes.find(n => n.id === selectedState)?.metadata && (
            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-gray-700 uppercase">Métadonnées</h5>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(nodes.find(n => n.id === selectedState).metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-500">{key}:</span>
                    <span className="text-gray-900 font-medium">
                      {typeof value === 'boolean' ? (value ? 'Oui' : 'Non') : 
                       Array.isArray(value) ? value.join(', ') : 
                       String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Composant pour afficher les détails d'un processus
const ProcessDetail = ({ process }) => {
  const [activeTab, setActiveTab] = useState('diagram');
  const [searchTerm, setSearchTerm] = useState('');

  console.log('ProcessDetail render - process:', process?.name, process?.id);

  // Réinitialiser les états quand le processus change
  useEffect(() => {
    console.log('ProcessDetail useEffect - process changed:', process?.name, process?.id);
    setActiveTab('diagram');
    setSearchTerm('');
  }, [process.id]);

  // Filtrer les états et transitions selon la recherche
  const filteredStates = process.states?.filter(state => 
    state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    state.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredTransitions = process.transitions?.filter(transition =>
    transition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transition.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transition.to.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="flex items-center space-x-3">
              <Workflow className="h-6 w-6 text-blue-500" />
              <span>{process.name}</span>
              <Badge variant="info">v{process.version}</Badge>
            </CardTitle>
            <p className="text-gray-600 mt-2">{process.description}</p>
            
            {/* Métadonnées */}
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Users className="h-4 w-4" />
                <span>{process.owner}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Layers className="h-4 w-4" />
                <span>{process.category}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span>{new Date(process.metadata?.lastUpdate || process.lastUpdate).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Tags */}
            {process.tags && process.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {process.tags.map((tag, index) => (
                  <Badge key={index} variant="default">{tag}</Badge>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex space-x-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-1" />
              Exporter
            </Button>
            <Button variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-1" />
              Modifier
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Onglets */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            {[
              { id: 'diagram', label: 'Diagramme', icon: GitBranch },
              { id: 'states', label: `États (${process.states?.length || 0})`, icon: Square },
              { id: 'transitions', label: `Transitions (${process.transitions?.length || 0})`, icon: ArrowRight },
              { id: 'json', label: 'JSON', icon: Code }
            ].map(tab => (
              <button
                key={tab.id}
                className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Barre de recherche pour les onglets états et transitions */}
        {(activeTab === 'states' || activeTab === 'transitions') && (
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Rechercher dans les ${activeTab === 'states' ? 'états' : 'transitions'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Contenu des onglets */}
        <div className="px-6 py-6">
          {activeTab === 'diagram' && (
            <ProcessDiagram process={process} />
          )}

          {activeTab === 'states' && (
            <div className="space-y-4">
              {filteredStates.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {searchTerm ? 'Aucun état trouvé' : 'Aucun état défini'}
                </p>
              ) : (
                filteredStates.map((state) => (
                  <Card key={state.id} className="overflow-hidden">
                    <div className={`h-2 ${state.isInitial ? 'bg-green-500' : state.isFinal ? 'bg-blue-500' : 'bg-gray-300'}`} />
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                            <span>{state.name}</span>
                            {state.isInitial && <Badge variant="success">Initial</Badge>}
                            {state.isFinal && <Badge variant="info">Final</Badge>}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{state.description}</p>
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: <code className="bg-gray-100 px-1 rounded">{state.id}</code>
                        </div>
                      </div>

                      {state.metadata && (
                        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                          {state.metadata.slaMinutes && (
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4 text-gray-400" />
                              <span>SLA: {state.metadata.slaMinutes} minutes</span>
                            </div>
                          )}
                          {state.metadata.notifications && (
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="h-4 w-4 text-gray-400" />
                              <span>Notifications: {state.metadata.notifications.join(', ')}</span>
                            </div>
                          )}
                          {state.metadata.manual_review && (
                            <div className="flex items-center space-x-2">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span>Revue manuelle requise</span>
                            </div>
                          )}
                          {state.metadata.ai_processing && (
                            <div className="flex items-center space-x-2">
                              <Bot className="h-4 w-4 text-gray-400" />
                              <span>Traitement IA</span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {activeTab === 'transitions' && (
            <div className="space-y-4">
              {filteredTransitions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {searchTerm ? 'Aucune transition trouvée' : 'Aucune transition définie'}
                </p>
              ) : (
                filteredTransitions.map((transition, index) => (
                  <Card key={index}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="bg-gray-100 px-3 py-1 rounded-lg text-sm font-medium">
                            {transition.from}
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" />
                          <div className="bg-gray-100 px-3 py-1 rounded-lg text-sm font-medium">
                            {transition.to}
                          </div>
                        </div>
                        <Badge variant={transition.type === 'automatic' ? 'success' : 'warning'}>
                          {transition.type}
                        </Badge>
                      </div>
                      
                      <div className="mt-3">
                        <h5 className="font-medium text-gray-900">{transition.name}</h5>
                        {transition.description && (
                          <p className="text-sm text-gray-600 mt-1">{transition.description}</p>
                        )}
                      </div>

                      {transition.conditions && transition.conditions.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-xs font-semibold text-gray-700 uppercase mb-2">Conditions</h6>
                          <ul className="space-y-1">
                            {transition.conditions.map((condition, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-start">
                                <Check className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                {condition.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {transition.actions && transition.actions.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-xs font-semibold text-gray-700 uppercase mb-2">Actions</h6>
                          <ul className="space-y-1">
                            {transition.actions.map((action, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-start">
                                <Zap className="h-3 w-3 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                                <span>
                                  <span className="font-medium">{action.type}:</span> {action.description}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {activeTab === 'json' && (
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(process, null, 2));
                  // TODO: Ajouter un feedback visuel
                }}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copier
              </Button>
              <pre className="bg-gray-50 rounded-lg p-4 overflow-x-auto text-sm">
                <code className="language-json">
                  {JSON.stringify(process, null, 2)}
                </code>
              </pre>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Composant principal
const ProcessDocumentation = () => {
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Fonction pour gérer le clic sur un processus
  const handleProcessClick = (process) => {
    console.log('=== HANDLE PROCESS CLICK ===');
    console.log('Process to select:', process);
    console.log('Current selected:', selectedProcess);
    console.log('Are they the same?', selectedProcess?.id === process.id);
    
    // Forcer la mise à jour même si c'est le même processus
    setSelectedProcess(null); // Reset d'abord
    setTimeout(() => {
      setSelectedProcess(process);
    }, 0);
  };

  // Charger tous les fichiers JSON du répertoire processes avec Webpack
  useEffect(() => {
    loadProcesses();
  }, []);

  // Debug: vérifier si selectedProcess change
  useEffect(() => {
    console.log('=== SELECTED PROCESS CHANGED ===');
    if (selectedProcess) {
      console.log('Name:', selectedProcess.name);
      console.log('ID:', selectedProcess.id);
      console.log('Filename:', selectedProcess.filename);
      console.log('Data exists:', !!selectedProcess.data);
      console.log('Data ID:', selectedProcess.data?.id);
      console.log('Data Name:', selectedProcess.data?.name);
    }
  }, [selectedProcess]);

  const loadProcesses = () => {
    setLoading(true);
    setErrors([]);
    const loadedProcesses = [];
    const loadErrors = [];

    try {
      // Utiliser processContext défini en haut du fichier
      // Parcourir tous les fichiers trouvés automatiquement
      processContext.keys().forEach(key => {
        const filename = key.replace('./', '');
        
        try {
          // Charger le fichier
          const data = processContext(key);
          
          // Valider la structure
          const validation = validateProcessStructure(data, filename);
          
          if (validation.isValid) {
            // Extraire les métadonnées
            const metadata = extractProcessMetadata(data, filename);
            
            loadedProcesses.push({
              ...metadata,
              data,
              icon: metadata.icon
            });
          } else {
            loadErrors.push({
              filename,
              errors: validation.errors,
              type: 'validation'
            });
          }
        } catch (error) {
          loadErrors.push({
            filename,
            errors: [error.message],
            type: 'loading'
          });
        }
      });

      // Tri initial par nom
      loadedProcesses.sort((a, b) => a.name.localeCompare(b.name));
      
      setProcesses(loadedProcesses);
      setErrors(loadErrors);
      
      // Sélectionner le premier processus par défaut seulement si aucun n'est sélectionné
      if (loadedProcesses.length > 0 && !selectedProcess) {
        setSelectedProcess(loadedProcesses[0]);
      }
    } catch (error) {
      setErrors([{
        filename: 'global',
        errors: ['Erreur lors du chargement des processus: ' + error.message],
        type: 'critical'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer et trier les processus
  const filteredAndSortedProcesses = useMemo(() => {
    let filtered = processes;

    // Filtrer par recherche
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrer par catégorie
    if (filterCategory !== 'all') {
      filtered = filtered.filter(p => p.category === filterCategory);
    }

    // Trier
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'date':
          return new Date(b.lastUpdate) - new Date(a.lastUpdate);
        case 'states':
          return b.statesCount - a.statesCount;
        default:
          return 0;
      }
    });
  }, [processes, searchTerm, filterCategory, sortBy]);

  // Obtenir les catégories uniques
  const categories = useMemo(() => {
    const cats = new Set(processes.map(p => p.category));
    return ['all', ...Array.from(cats)];
  }, [processes]);

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des processus...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Documentation des Processus
            </h1>
            <p className="mt-2 text-gray-600">
              {processes.length} processus disponibles
              {errors.length > 0 && (
                <span className="text-red-600 ml-2">
                  ({errors.length} erreur{errors.length > 1 ? 's' : ''})
                </span>
              )}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={loadProcesses}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau processus
            </Button>
          </div>
        </div>
      </div>

      {/* Afficher les erreurs si présentes */}
      {errors.length > 0 && (
        <div className="mb-6 space-y-2">
          {errors.map((error, index) => (
            <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900">
                    Erreur {error.type} - {error.filename}
                  </h4>
                  <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                    {error.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barre de filtres et recherche */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un processus..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'Toutes les catégories' : cat}
            </option>
          ))}
        </select>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="name">Trier par nom</option>
          <option value="category">Trier par catégorie</option>
          <option value="date">Trier par date</option>
          <option value="states">Trier par nombre d'états</option>
        </select>
      </div>

      {/* Liste des processus disponibles */}
      {filteredAndSortedProcesses.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {searchTerm || filterCategory !== 'all' 
                ? 'Aucun processus ne correspond à vos critères'
                : 'Aucun processus disponible'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {filteredAndSortedProcesses.map((process, index) => {
              const Icon = process.icon;
              const isSelected = selectedProcess?.filename === process.filename;
              
              return (
                <Card
                  key={process.filename}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('=== CLICK EVENT ===');
                    console.log('Clicking on:', process.name, process.filename);
                    console.log('Process object:', process);
                    console.log('Current selectedProcess:', selectedProcess?.name);
                    handleProcessClick(process);
                  }}
                  onMouseDown={() => console.log('Mouse down on:', process.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <CardContent 
                    className="p-4"
                    onClick={(e) => {
                      console.log('=== CARDCONTENT CLICK ===');
                      e.stopPropagation();
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${
                        isSelected ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        <Icon className={`h-6 w-6 ${
                          isSelected ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {process.name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {process.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                          <span className="flex items-center">
                            <Square className="h-3 w-3 mr-1" />
                            {process.statesCount} états
                          </span>
                          <span className="flex items-center">
                            <ArrowRight className="h-3 w-3 mr-1" />
                            {process.transitionsCount} transitions
                          </span>
                        </div>
                        {process.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {process.tags.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="default" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {process.tags.length > 3 && (
                              <Badge variant="default" className="text-xs">
                                +{process.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Détails du processus sélectionné */}
          {(() => {
            console.log('=== RENDER PROCESS DETAIL ===');
            console.log('selectedProcess:', selectedProcess);
            console.log('selectedProcess.data:', selectedProcess?.data);
            
            if (selectedProcess && selectedProcess.data) {
              return (
                <ProcessDetail 
                  key={`${selectedProcess.id}-${selectedProcess.filename}`} 
                  process={selectedProcess.data} 
                />
              );
            }
            return null;
          })()}
        </>
      )}
    </div>
  );
};

// Ajout d'une icône manquante
const Minus = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

export default ProcessDocumentation;